<main id="conteudo" class="reputation-shell" tabindex="-1">
  @if (busy()) {
  <div class="loading-overlay" role="status" aria-live="polite">
    <div class="spinner"></div>
    <p>{{ syncing() ? 'Conferindo devoluções e reputação...' : 'Carregando relatório...' }}</p>
    @if (syncProgress(); as progress) {
    <small>{{ progress.processed }} de {{ progress.total || '?' }} reclamações/devoluções processadas</small>
    }
  </div>
  }

  <header class="card reputation-hero">
    <div>
      <span class="eyebrow">Reputação Mercado Livre</span>
      <h1>Devoluções e Reclamações</h1>
      <p>{{ periodLabel() }} · entenda quais devoluções afetaram a reputação e por quê.</p>
    </div>
    <div class="hero-actions no-print">
      <a class="btn btn-secondary" routerLink="/dashboard">Conciliação</a>
      <a class="btn btn-secondary" routerLink="/relatorios">Relatórios</a>
      <a class="btn btn-secondary" routerLink="/anuncios">Anúncios</a>
      <a class="btn btn-secondary" routerLink="/publicidade">Publicidade</a>
      <button class="btn btn-secondary" type="button" (click)="logout()">Sair</button>
    </div>
  </header>

  <form class="card filters-card no-print" (ngSubmit)="loadReport()" aria-describedby="reputation-filter-note">
    <label for="reputation-start-date"><span>Data inicial</span><input id="reputation-start-date" name="startDate" type="date" [value]="startDate()" (input)="startDate.set($any($event.target).value)" /></label>
    <label for="reputation-end-date"><span>Data final</span><input id="reputation-end-date" name="endDate" type="date" [value]="endDate()" (input)="endDate.set($any($event.target).value)" /></label>
    <button class="btn btn-primary" type="submit" [disabled]="busy()">Carregar do banco</button>
    <button class="btn btn-secondary" type="button" [disabled]="busy()" (click)="syncReport()">Atualizar pela API</button>
    <button class="btn btn-secondary" type="button" [disabled]="busy()" (click)="setDefaultPeriod(); loadReport()">Mês atual</button>
    <button class="btn btn-secondary" type="button" [disabled]="busy()" (click)="setLast30Days()">Últimos 30 dias</button>
    <p id="reputation-filter-note">O botão de API consulta reclamações/devoluções oficiais e pergunta ao Mercado Livre se cada uma afetou a reputação.</p>
  </form>

  @if (message()) { <section class="card info-card">{{ message() }}</section> }
  @if (error()) { <section class="card info-card error">{{ error() }}</section> }

  @if (report(); as report) {
  <section class="card section-card seller-reputation-card">
    <div class="section-head">
      <div>
        <span class="eyebrow">Termômetro oficial do vendedor</span>
        <h2>Reputação atual no Mercado Livre</h2>
        <p>
          Este bloco vem direto do cadastro do vendedor no ML. Se a lista de reclamações vier vazia,
          mas a reputação mudou, normalmente o motivo aparece nestas métricas agregadas.
        </p>
      </div>
      @if (report.sellerReputation?.syncedAt) {
      <span class="pill neutral">Atualizado em {{ formatDateTime(report.sellerReputation?.syncedAt) }}</span>
      }
    </div>

    @if (report.sellerReputation; as reputation) {
    <div class="kpi-grid compact">
      <article class="card kpi">
        <span>Nível oficial</span>
        <strong>{{ reputationLevelLabel(reputation.levelId || reputation.realLevel) }}</strong>
      </article>
      <article class="card kpi">
        <span>Status MercadoLíder</span>
        <strong>{{ powerSellerLabel(reputation.powerSellerStatus) }}</strong>
      </article>
      <article class="card kpi">
        <span>Vendas concluídas</span>
        <strong>{{ reputation.transactions.completed }}</strong>
      </article>
      <article class="card kpi danger">
        <span>Vendas canceladas</span>
        <strong>{{ reputation.transactions.canceled }}</strong>
      </article>
    </div>

    <div class="reputation-metrics">
      <article>
        <span>Reclamações</span>
        <strong>{{ formatPercent(reputation.metrics.claims.rate) }}</strong>
        <small>{{ reputation.metrics.claims.value }} caso(s) considerados pelo ML</small>
      </article>
      <article>
        <span>Cancelamentos</span>
        <strong>{{ formatPercent(reputation.metrics.cancellations.rate) }}</strong>
        <small>{{ reputation.metrics.cancellations.value }} caso(s) considerados pelo ML</small>
      </article>
      <article>
        <span>Atraso no envio/manuseio</span>
        <strong>{{ formatPercent(reputation.metrics.delayedHandlingTime.rate) }}</strong>
        <small>{{ reputation.metrics.delayedHandlingTime.value }} caso(s) considerados pelo ML</small>
      </article>
    </div>
    } @else {
    <p class="empty">Ainda não temos o termômetro oficial salvo. Clique em “Atualizar pela API”.</p>
    }
  </section>

  <section class="kpi-grid">
    <article class="card kpi"><span>Total de reclamações/devoluções</span><strong>{{ report.totals.claimsCount }}</strong></article>
    <article class="card kpi danger"><span>Afetaram reputação</span><strong>{{ report.totals.affectedCount }}</strong></article>
    <article class="card kpi warning"><span>Ainda podem afetar</span><strong>{{ report.totals.incentiveCount }}</strong></article>
    <article class="card kpi"><span>Em aberto</span><strong>{{ report.totals.openCount }}</strong></article>
    <article class="card kpi"><span>Valor envolvido</span><strong>{{ formatMoney(report.totals.totalAmount) }}</strong></article>
    <article class="card kpi"><span>Custo de devolução</span><strong>{{ formatMoney(report.totals.totalReturnCost) }}</strong></article>
  </section>

  <section class="card section-card">
    <span class="eyebrow">Prioridade máxima</span>
    <h2>Casos que afetaram ou podem afetar reputação</h2>
    @if (report.attentionClaims.length > 0) {
    <div class="claim-list">
      @for (claim of report.attentionClaims; track claim.claimId) {
      <article class="claim-card">
        <div class="claim-head">
          <span class="pill" [ngClass]="reputationClass(claim.affectsReputation)">{{ reputationLabel(claim.affectsReputation) }}</span>
          <span class="muted">Claim {{ claim.claimId }}</span>
          <span class="muted">{{ formatDateTime(claim.dateCreated) }}</span>
        </div>
        <h3>{{ claimTitle(claim) }}</h3>
        <div class="claim-meta">
          <span>{{ claimReference(claim) }}</span>
          <span>Status: {{ statusLabel(claim.status) }}</span>
          <span>Valor: {{ claimAmountLabel(claim) }}</span>
          <span>Categoria: {{ claim.riskCategory || 'A conferir' }}</span>
        </div>
        <p><strong>Motivo:</strong> {{ claim.simpleReason || claim.reasonName || claim.reasonId || 'Sem motivo informado' }}</p>
        <p><strong>Próxima ação:</strong> {{ claim.recommendation || 'Conferir manualmente no Mercado Livre.' }}</p>
        @if (claim.dueDate) {
        <p class="deadline">Prazo informado pelo ML: {{ formatDateTime(claim.dueDate) }}</p>
        }
      </article>
      }
    </div>
    } @else {
    <p class="empty">Nenhum caso crítico salvo para este período.</p>
    }
  </section>

  <section class="card section-card two-columns">
    <div>
      <span class="eyebrow">Motivos</span>
      <h2>Por que os clientes devolveram/reclamaram</h2>
      <table>
        <caption class="visually-hidden">Motivos de reclamações e devoluções</caption>
        <thead><tr><th scope="col">Motivo</th><th scope="col">Qtd</th><th scope="col">Afetou</th><th scope="col">Valor</th></tr></thead>
        <tbody>
          @for (reason of report.topReasons; track reason.reason) {
          <tr><td>{{ reason.reason }}</td><td>{{ reason.count }}</td><td>{{ reason.affected }}</td><td>{{ formatMoney(reason.amount) }}</td></tr>
          }
        </tbody>
      </table>
    </div>
    <div>
      <span class="eyebrow">Produtos</span>
      <h2>Produtos com mais reclamações</h2>
      <table>
        <caption class="visually-hidden">Produtos com mais reclamações ou devoluções</caption>
        <thead><tr><th scope="col">Produto</th><th scope="col">Qtd</th><th scope="col">Afetou</th><th scope="col">Valor</th></tr></thead>
        <tbody>
          @for (product of report.topProducts; track product.itemId || product.itemTitle) {
          <tr><td>{{ product.itemTitle }}</td><td>{{ product.count }}</td><td>{{ product.affected }}</td><td>{{ formatMoney(product.amount) }}</td></tr>
          }
        </tbody>
      </table>
    </div>
  </section>

  <section class="card section-card">
    <span class="eyebrow">Conferência completa</span>
    <h2>Todas as reclamações/devoluções encontradas</h2>
    <div class="table-wrap">
      <table>
        <caption class="visually-hidden">Todas as reclamações e devoluções encontradas</caption>
        <thead>
          <tr>
            <th scope="col">Data</th>
            <th scope="col">Pedido/envio</th>
            <th scope="col">Produto</th>
            <th scope="col">Reputação</th>
            <th scope="col">Motivo ML</th>
            <th scope="col">Explicação simples</th>
            <th scope="col">Valor</th>
          </tr>
        </thead>
        <tbody>
          @for (claim of report.claims; track claim.claimId) {
          <tr>
            <td>{{ formatDateTime(claim.dateCreated) }}</td>
            <td>{{ claimReference(claim) }}</td>
            <td>{{ claimTitle(claim) }}</td>
            <td><span class="pill" [ngClass]="reputationClass(claim.affectsReputation)">{{ reputationLabel(claim.affectsReputation) }}</span></td>
            <td>{{ claim.reasonName || claim.reasonId || '-' }}</td>
            <td>{{ claim.simpleReason || '-' }}</td>
            <td>{{ claimAmountLabel(claim) }}</td>
          </tr>
          }
        </tbody>
      </table>
    </div>
  </section>
  }
</main>
